import { idDeSvg, mensajeDeErrorMermaid, OPCIONES_MERMAID } from "@gestiondoc/plantilla";
import { type Contenido, flujogramasDe } from "@gestiondoc/shared";
import { useEffect, useState } from "react";

/**
 * Mermaid en el navegador: dibuja el código de cada bloque `flujograma` como SVG para la vista
 * previa y para el editor. La librería pesa, así que se carga la primera vez que hace falta;
 * los resultados se guardan por (id, código) para no redibujar en cada tecla.
 */

type Mermaid = typeof import("mermaid").default;
let mermaidCargado: Promise<Mermaid> | null = null;

function cargarMermaid(): Promise<Mermaid> {
  if (!mermaidCargado) {
    mermaidCargado = import("mermaid").then((m) => {
      m.default.initialize({ ...OPCIONES_MERMAID });
      return m.default;
    });
  }
  return mermaidCargado;
}

export type Dibujo = { ok: true; svg: string } | { ok: false; error: string };

const cache = new Map<string, Promise<Dibujo>>();

/** El SVG de un bloque, o el error de sintaxis que Mermaid reporta. */
export function dibujarFlujograma(id: string, codigo: string): Promise<Dibujo> {
  const clave = `${id}\n${codigo}`;
  let d = cache.get(clave);
  if (!d) {
    d = (async () => {
      try {
        const mermaid = await cargarMermaid();
        // `parse` antes de `render`: un código roto no deja restos en el DOM.
        await mermaid.parse(codigo);
        const { svg } = await mermaid.render(idDeSvg(id), codigo);
        return { ok: true as const, svg };
      } catch (e) {
        return { ok: false as const, error: mensajeDeErrorMermaid(e) };
      }
    })();
    cache.set(clave, d);
    if (cache.size > 200) {
      const primera = cache.keys().next().value;
      if (primera !== undefined) cache.delete(primera);
    }
  }
  return d;
}

/**
 * Los SVG de todos los flujogramas del contenido, para `DatosDeHoja.svgs`. Sin contenido (todavía
 * cargando, o una hoja sobre un PDF) no hay nada que dibujar.
 */
export function useFlujogramas(contenido: Contenido | null | undefined): Record<string, string> {
  // La clave resume id y código de cada bloque: el efecto corre solo cuando cambia un diagrama,
  // no en cada tecla del resto del documento.
  const clave = (contenido ? flujogramasDe(contenido) : [])
    .map((b) => `${b.id}\n${b.codigo}`)
    .join("\u0000");
  const [svgs, setSvgs] = useState<Record<string, string>>({});
  useEffect(() => {
    let vigente = true;
    const bloques = clave
      ? clave.split("\u0000").map((x) => {
          const [id = "", ...resto] = x.split("\n");
          return { id, codigo: resto.join("\n") };
        })
      : [];
    if (bloques.length === 0) {
      setSvgs({});
      return;
    }
    Promise.all(bloques.map((b) => dibujarFlujograma(b.id, b.codigo))).then((dibujos) => {
      if (!vigente) return;
      const mapa: Record<string, string> = {};
      bloques.forEach((b, i) => {
        const d = dibujos[i];
        if (d?.ok) mapa[b.id] = d.svg;
      });
      setSvgs(mapa);
    });
    return () => {
      vigente = false;
    };
  }, [clave]);
  return svgs;
}
